import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../store/store';
import axiosInstance from '../../services/axios';
import { ICompanyDetails, IPaymentMethods } from '../common/models/orderData';
import { hideLoader, hideLoaderFailure, showLoader } from '../layout/slice';
import actionTypes from './actionTypes';
import { IOrder } from './models/details';
import { mapBasketToOrderRequest } from './orderMappers';
import { clearBasket } from '../basket/slice';
import i18n from '../../i18n';
import moment from 'moment';
import { resolveGatewayID } from './utils/paymentMethods';

export const createOrder = createAsyncThunk(
  actionTypes.CREATE_ORDER,
  async (promoCode: string | undefined, thunkApi): Promise<IOrder | null> => {
    thunkApi.dispatch(showLoader());
    const store = thunkApi.getState() as RootState;

    const hasExpiredItems = store.basket.items.some((item) => {
      const itemDate = item.date ? moment(item.date) : moment(item.startDate);
      return itemDate.isBefore(moment().startOf('day'));
    });

    if (hasExpiredItems) {
      thunkApi.dispatch(hideLoader());
      thunkApi.dispatch(
        hideLoaderFailure(i18n.t('common:expired_items_error'))
      );
      thunkApi.dispatch(clearBasket());
      return null;
    }

    const orderRequest = mapBasketToOrderRequest(store.basket);

    const params = new URLSearchParams();
    if (promoCode) params.set('promoCode', promoCode);

    const selectedId = store.order.selectedId ?? null;
    const gatewayID = resolveGatewayID(selectedId, store.order.paymentMethods);

    if (gatewayID != null) params.set('gatewayID', String(gatewayID));

    const url = `/v1/order${params.toString() ? `?${params.toString()}` : ''}`;

    try {
      const response = await axiosInstance.post<IOrder>(url, orderRequest);
      thunkApi.dispatch(hideLoader());

      if (response.status !== 200) {
        thunkApi.dispatch(hideLoaderFailure(i18n.t('common:error_message')));
        return null;
      }

      thunkApi.dispatch(clearBasket());
      return response.data;
    } catch (e: any) {
      const errorDetails = {
        message: e?.message || 'Unknown error',
        response: e?.response
          ? { data: e.response.data, status: e.response.status }
          : undefined,
      };

      console.error('Order creation error:', errorDetails);

      thunkApi.dispatch(
        hideLoaderFailure(
          errorDetails.response?.data?.message || i18n.t('common:error_message')
        )
      );
      return null;
    }
  }
);

export const fetchOrderDetails = createAsyncThunk(
  actionTypes.GET_ORDER_DETAILS,
  async (orderId: string, thunkApi): Promise<IOrder> => {
    thunkApi.dispatch(showLoader());
    try {
      const response = await axiosInstance.get(`/v1/order/${orderId}`);

      thunkApi.dispatch(hideLoader());

      return response.data;
    } catch (e) {
      console.error(JSON.stringify(e));
      // TODO: create right error message
      thunkApi.dispatch(hideLoaderFailure(i18n.t('common:error_message')));

      throw e;
    }
  }
);

export const getCompanyData = async (nip: string): Promise<ICompanyDetails> => {
  try {
    const response = await axiosInstance.get<ICompanyDetails>(
      '/v1/order/invoice-data',
      {
        params: { nip },
      }
    );

    return response.data;
  } catch (e) {
    console.error(JSON.stringify(e));
    throw e;
  }
};

export const getPaymentMethods = createAsyncThunk<IPaymentMethods[]>(
  actionTypes.GET_PAYMENT_METHODS,
  async (_, thunkApi): Promise<IPaymentMethods[]> => {
    thunkApi.dispatch(showLoader());
    try {
      const response = await axiosInstance.get<IPaymentMethods[]>(
        '/v1/order/payment/methods'
      );

      thunkApi.dispatch(hideLoader());

      return response.data;
    } catch (e) {
      console.error(JSON.stringify(e));
      // TODO: create right error message
      thunkApi.dispatch(hideLoaderFailure(i18n.t('common:error_message')));
      throw e;
    }
  }
);
